/**
 * Custom look of the public career site (job board): one set of colours and images per visitor
 * mode (light / dark), plus shapes and fonts shared by both modes. Saved on the firm (`jobBoardTheme`)
 * by the job board settings and sent to the career pages.
 * Every value left to `null` keeps the default BestConsultant look.
 */
import { sanitizeCareerImage } from './careerImage.ts'

export const CAREER_THEME_MODES = ['light', 'dark'] as const
export type TCareerThemeModeKey = (typeof CAREER_THEME_MODES)[number]

export const CAREER_THEME_COLOR_KEYS = [
  'primary',
  'pageBackground',
  'surface',
  'border',
  'text',
  'mutedText',
  'heroBackground',
  'heroBackgroundEnd',
  'heroText',
  'buttonBackground',
  'buttonText',
  'labelBackground',
  'labelText',
] as const
export type TCareerThemeColorKey = (typeof CAREER_THEME_COLOR_KEYS)[number]

export const CAREER_THEME_IMAGE_KEYS = ['pageBackgroundImage', 'heroBackgroundImage'] as const
export type TCareerThemeImageKey = (typeof CAREER_THEME_IMAGE_KEYS)[number]

export const CAREER_BUTTON_RADII = ['none', 'sm', 'md', 'lg', 'full'] as const
export type TCareerButtonRadius = (typeof CAREER_BUTTON_RADII)[number]

export const CAREER_CARD_RADII = ['none', 'sm', 'md', 'lg', 'xl', '2xl'] as const
export type TCareerCardRadius = (typeof CAREER_CARD_RADII)[number]

export const CAREER_CARD_SHADOWS = ['none', 'sm', 'md', 'lg'] as const
export type TCareerCardShadow = (typeof CAREER_CARD_SHADOWS)[number]

/** Fonts offered to the career site, loaded from Bunny Fonts (no visitor tracking, GDPR-friendly) */
export const CAREER_THEME_FONTS = [
  { family: 'DM Sans', category: 'sans-serif' },
  { family: 'IBM Plex Sans', category: 'sans-serif' },
  { family: 'Inter', category: 'sans-serif' },
  { family: 'Manrope', category: 'sans-serif' },
  { family: 'Montserrat', category: 'sans-serif' },
  { family: 'Nunito', category: 'sans-serif' },
  { family: 'Open Sans', category: 'sans-serif' },
  { family: 'Outfit', category: 'sans-serif' },
  { family: 'Plus Jakarta Sans', category: 'sans-serif' },
  { family: 'Poppins', category: 'sans-serif' },
  { family: 'Raleway', category: 'sans-serif' },
  { family: 'Roboto', category: 'sans-serif' },
  { family: 'Rubik', category: 'sans-serif' },
  { family: 'Source Sans 3', category: 'sans-serif' },
  { family: 'Space Grotesk', category: 'sans-serif' },
  { family: 'Work Sans', category: 'sans-serif' },
  { family: 'Lora', category: 'serif' },
  { family: 'Merriweather', category: 'serif' },
  { family: 'Playfair Display', category: 'serif' },
  { family: 'Roboto Slab', category: 'serif' },
] as const
export type TCareerThemeFont = (typeof CAREER_THEME_FONTS)[number]

export type TCareerThemeModeValues = Record<TCareerThemeColorKey | TCareerThemeImageKey, string | null>

export type TCareerTheme = {
  light: TCareerThemeModeValues
  dark: TCareerThemeModeValues
  buttonRadius: TCareerButtonRadius | null
  cardRadius: TCareerCardRadius | null
  cardShadow: TCareerCardShadow | null
  fontFamily: string | null
  headingFontFamily: string | null
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const sanitizeColor = (value: unknown) =>
  typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim().toLowerCase() : null

/** An uploaded image or an http(s) URL (see sanitizeCareerImage) */
export const sanitizeCareerThemeImageUrl = sanitizeCareerImage

const pick = <T extends string>(allowed: readonly T[], value: unknown): T | null =>
  allowed.includes(value as T) ? value as T : null

export const findCareerThemeFont = (family: unknown) =>
  CAREER_THEME_FONTS.find(font => font.family === family) || null

const sanitizeMode = (value: unknown): TCareerThemeModeValues => {
  const input = isRecord(value) ? value : {}
  const colors = CAREER_THEME_COLOR_KEYS.map(key => [key, sanitizeColor(input[key])])
  const images = CAREER_THEME_IMAGE_KEYS.map(key => [key, sanitizeCareerThemeImageUrl(input[key])])
  return Object.fromEntries([...colors, ...images]) as TCareerThemeModeValues
}

/** Keeps only the known keys with a valid value: anything else becomes `null` (default look) */
export const sanitizeCareerTheme = (value: unknown): TCareerTheme => {
  const input = isRecord(value) ? value : {}
  return {
    light: sanitizeMode(input.light),
    dark: sanitizeMode(input.dark),
    buttonRadius: pick(CAREER_BUTTON_RADII, input.buttonRadius),
    cardRadius: pick(CAREER_CARD_RADII, input.cardRadius),
    cardShadow: pick(CAREER_CARD_SHADOWS, input.cardShadow),
    fontFamily: findCareerThemeFont(input.fontFamily)?.family || null,
    headingFontFamily: findCareerThemeFont(input.headingFontFamily)?.family || null,
  }
}

export const emptyCareerTheme = () => sanitizeCareerTheme(null)

/** Whether at least one value differs from the default look */
export const hasCareerTheme = (value: unknown) => {
  const theme = sanitizeCareerTheme(value)
  const { light, dark, ...shared } = theme
  return [...Object.values(light), ...Object.values(dark), ...Object.values(shared)].some(item => item !== null)
}
